import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { Map } from 'immutable';

import {
  ALL_READING_INTENTIONS,
  ReadingIntention,
  ReadingIntentionsKey,
  SaveableStudyNote,
} from '®shared/annotations';
import {
  OnlineResource,
  isOnlineResourcePreview,
} from '®shared/bibliographic-resource';
import { AppConfig } from '®shared/config';
import { ContainerModes, LIST_ID } from '®shared/container-structure/common';
import { EditableItem } from '®shared/reading-list/models';
import { Importance } from '®shared/tenant';
import {
  ReadingIntentionsByResourceId,
  StudyNotesByResourceId,
} from '®shared/user-annotations';

import {
  ALL_COURSE_RESERVE_STATUS_FILTERS,
  CourseReserveFilterStatus,
  CourseReserveStatusFilterAndLabel,
} from './course-reserve-status-filter-and-label';
import {
  ALL_DIGITISATION_STATUS_FILTERS,
  DigitisationFilterStatus,
  DigitisationStatusFilterAndLabel,
} from './digitisation-status-filter-and-label';
import { QuickFilter } from './quick-filter';
import { QuickFiltersService } from './quick-filters.service';
import {
  RESOURCE_TYPE_FILTER,
  ResourceTypeFilter,
} from './resource-type-filter';
import { STUDY_NOTE_FILTER, StudyNoteFilter } from './study-note-filter';

type StudyNotesByResourceIdAliased = StudyNotesByResourceId;
type ReadingIntentionsByResourceIdAliased = ReadingIntentionsByResourceId;

export interface ItemCountByQuickFilters {
  totalItems: number;
  itemCountByImportance: Map<string, number>;
  itemCountByPhysicalOrOnlineResourceType: Map<boolean, number>;
  itemCountByDigitisationStatus: Map<DigitisationFilterStatus, number>;
  itemCountByCourseReserveStatus: Map<CourseReserveFilterStatus, number>;
  itemCountByStudyNote: Map<boolean, number>;
  itemCountByReadingIntention: Map<ReadingIntentionsKey, number>;
  itemCountForHasLibraryNote: number;
}

export type QuickFilterEvent = { quickFilter?: QuickFilter };

/**
 * Component offering a menu of quick filters, which affect which list items are displayed in a list.
 */
@Component({
  providers: [QuickFiltersService],
  selector: 'rl-quick-filters-menu',
  templateUrl: './quick-filters-menu.component.html',
})
export class QuickFiltersMenuComponent implements OnChanges {
  ReadingIntentionsKey = ReadingIntentionsKey;

  @Input() areAnnotationsResolved?: boolean;
  @Input() readingIntentionsByResource?: ReadingIntentionsByResourceIdAliased;
  @Input() studyNotesByResource?: StudyNotesByResourceIdAliased;
  @Input() onlineResourcesByItem: Map<string, OnlineResource>;
  @Input() importances: Importance[];
  @Input() quickFilter?: QuickFilter;
  @Input() containerMode: ContainerModes;
  @Input() itemCount: ItemCountByQuickFilters;
  @Output()
  quickFilterUpdate: EventEmitter<QuickFilterEvent> = new EventEmitter();
  @Output() clearAll: EventEmitter<any> = new EventEmitter();

  readingIntentions: ReadingIntention[];
  studyNoteFilters: StudyNoteFilter[];
  resourceTypeFilters: ResourceTypeFilter[] = [
    RESOURCE_TYPE_FILTER.PHYSICAL,
    RESOURCE_TYPE_FILTER.ONLINE,
  ];
  digitisationStatusFilters: DigitisationStatusFilterAndLabel[];
  courseReserveStatusFilters: CourseReserveStatusFilterAndLabel[];
  isFilterLabelTruncated = false;
  showHasLibraryNoteFilter = false;

  readonly noImportanceSet =
    this.quickFiltersService.quickFilterForNoImportanceSet();

  constructor(
    @Inject(LIST_ID) public listId: string,
    private quickFiltersService: QuickFiltersService,
    private appConfig: AppConfig,
  ) {}

  ngOnChanges() {
    if (this.isEditMode()) {
      this.readingIntentions = [];
      this.studyNoteFilters = [];
      this.showHasLibraryNoteFilter = true;
    } else {
      this.readingIntentions = ALL_READING_INTENTIONS;
      this.studyNoteFilters = [
        STUDY_NOTE_FILTER.PRESENT,
        STUDY_NOTE_FILTER.ABSENT,
      ];
      this.showHasLibraryNoteFilter = false;
    }

    if (
      this.isEditMode() &&
      this.appConfig.tenant.featureFlags.isDigitisationWorkflowEnabled &&
      !this.appConfig.tenant.featureFlags.isCourseReserveWorkflowEnabled
    ) {
      this.digitisationStatusFilters = ALL_DIGITISATION_STATUS_FILTERS;
    } else {
      this.digitisationStatusFilters = [];
    }

    if (this.appConfig.tenant.featureFlags.isCourseReserveWorkflowEnabled) {
      this.courseReserveStatusFilters = ALL_COURSE_RESERVE_STATUS_FILTERS;
    } else {
      this.courseReserveStatusFilters = [];
    }
  }

  onQuickFilterByImportance(importance: Importance) {
    const quickFilter =
      this.quickFiltersService.quickFilterForImportance(importance);
    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByNoImportanceSet() {
    const quickFilter =
      this.quickFiltersService.quickFilterForNoImportanceSet();
    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByIntention(intention: ReadingIntention) {
    const quickFilter = this.quickFiltersService.quickFilterForIntention(
      intention,
      this.getReadingIntentionForItem.bind(this),
    );
    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByStudyNotePresent(studyNoteFilter: StudyNoteFilter) {
    const quickFilter = this.quickFiltersService.quickFilterForStudyNotePresent(
      studyNoteFilter,
      this.getStudyNoteForItem.bind(this),
    );
    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByResourceType(resourceTypeFilter: ResourceTypeFilter) {
    const quickFilter = this.quickFiltersService.quickFilterForResourceType(
      resourceTypeFilter,
      this.getOnlineResourceForItem.bind(this),
    );

    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByHasLibraryNote() {
    const quickFilter = this.quickFiltersService.quickFilterForLibraryNote();
    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByDigitisationStatus(
    digitisationStatusFilter: DigitisationStatusFilterAndLabel,
  ) {
    const quickFilter =
      this.quickFiltersService.quickFilterForDigitisationStatus(
        digitisationStatusFilter,
      );

    this.onUpdateQuickFilter(quickFilter);
  }

  onQuickFilterByCourseReserveStatus(
    courseReserveStatusFilter: CourseReserveStatusFilterAndLabel,
  ) {
    const quickFilter =
      this.quickFiltersService.quickFilterForCourseReserveStatus(
        courseReserveStatusFilter,
      );

    this.onUpdateQuickFilter(quickFilter);
  }

  private onUpdateQuickFilter(quickFilter: QuickFilter) {
    this.quickFilterUpdate.emit({ quickFilter });
  }

  onClearAll($event: any) {
    this.clearAll.emit($event);
  }

  private getResourceIdForItem(item: EditableItem): string | undefined {
    return item && item.resource && item.resource.id;
  }

  getReadingIntentionForItem(item: EditableItem): string | undefined {
    const resourceId = this.getResourceIdForItem(item);
    if (resourceId && this.readingIntentionsByResource) {
      const intention = this.readingIntentionsByResource[resourceId];
      return intention && intention.status;
    }

    return undefined;
  }

  getStudyNoteForItem(item: EditableItem): SaveableStudyNote | undefined {
    const resourceId = this.getResourceIdForItem(item);
    if (resourceId && this.studyNotesByResource) {
      return this.studyNotesByResource[resourceId];
    }

    return undefined;
  }

  getOnlineResourceForItem(item: EditableItem): OnlineResource | undefined {
    if (item.id && this.onlineResourcesByItem) {
      const onlineResource = this.onlineResourcesByItem.get(item.id);
      if (onlineResource && !isOnlineResourcePreview(onlineResource)) {
        return onlineResource;
      }
    }

    return undefined;
  }

  onFilterLabelTruncate(isTruncated: boolean) {
    this.isFilterLabelTruncated = isTruncated;
  }

  isEditMode() {
    return this.containerMode === ContainerModes.Edit;
  }
}
